/**
 * ScubaDeepScanTab: the on-demand CISA SCuBA assessment in the Carbon
 * Security Center layout (mockup 03):
 *   brief       the pillar brief (BriefHero via PillarBrief) from
 *               `composeScubaBrief` (lib/security-center/scuba-brief.ts),
 *               with "Run a new audit" beside it and a
 *               SCuBA snapshot tile (pass rate and a results meter) in place
 *               of a score tile: SCuBA does not roll into Guardian Score
 *   workloads   one framework-style tile per workload: pass rate, "{n} of
 *               {m} checks pass" and an 8px bar, in a condensed grid
 *   checks      a Carbon DataTable of every check: contained tabs by result
 *               with counts, search and a Workload select, severity by shape
 *               and word, and rows that expand into what SCuBA found and the
 *               baseline reference
 * Before the first audit, an action-first brief offers the audit itself.
 *
 * Functional preserves: scan-job polling, the scan kickoff, the dompurify
 * sanitization on `details`, the imperative `refresh()` / `startScan()` ref
 * API, and the cross-link to Best Practice.
 */

import { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import type {
  ScubaReport,
  ScubaProductResult,
  ScubaProductSummary,
  ScubaControlGroup,
  ScubaControl,
  ScubaControlResult,
  ScubaSummary,
} from '@/types'
import { getScubaProductInfo as getProductInfo } from '@/types/scuba'
import { SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import PillarBrief from '@/components/PostureBrief/PillarBrief'
import EmptyState from '@/components/shared/EmptyState'
import { composeScubaBrief } from '@/lib/security-center/scuba-brief'
import { BigNumber } from '@/components/carbon/BigNumber'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Button, ButtonLink } from '@/components/carbon/Button'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconCheck, IconExternal } from '@/components/carbon/icons'
import { RowDetail, RowDetailSection } from '@/components/carbon/RowDetail'
import { ScoreBar } from '@/components/carbon/ScoreBar'
import { SegmentedMeter } from '@/components/carbon/SegmentedMeter'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { severityFromApi } from '@/components/carbon/severity'
import { Skeleton } from '@/components/carbon/Skeleton'
import { TableSearch } from '@/components/carbon/TableSearch'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tag } from '@/components/carbon/Tag'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  EnvelopeIcon,
  FolderIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'

/** Ref interface for programmatic control of the SCuBA tab. */
export interface ScubaDeepScanTabRef {
  refresh: () => void
  startScan: () => void
}

export interface ScubaDeepScanTabProps {
  tenantId: string | undefined
  onStatusChange?: (status: 'idle' | 'scanning' | 'completed' | 'error') => void
}

/** Allow-listed HTML in `control.details` so vendor-supplied remediation links render as anchors. */
const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['a', 'br', 'strong', 'em', 'p', 'ul', 'ol', 'li', 'span', 'b', 'i'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ADD_ATTR: ['target', 'rel'],
  })
}

/** Heroicons drawn the Carbon way: 16px, square caps and miter joins, decorative. */
const ICON_PROPS = { className: 'h-4 w-4', strokeLinecap: 'square', strokeLinejoin: 'miter', 'aria-hidden': true } as const

const getProductIcon = (productName: string) => {
  switch (productName.toLowerCase()) {
    case 'aad':         return <ShieldCheckIcon {...ICON_PROPS} />
    case 'sharepoint':  return <FolderIcon {...ICON_PROPS} />
    case 'exo':         return <EnvelopeIcon {...ICON_PROPS} />
    case 'teams':       return <ChatBubbleLeftRightIcon {...ICON_PROPS} />
    case 'defender':    return <ShieldExclamationIcon {...ICON_PROPS} />
    default:            return <ShieldCheckIcon {...ICON_PROPS} />
  }
}

const n = (v: number) => v.toLocaleString('en-US')
const plural = (count: number, one: string, many: string) => (count === 1 ? one : many)

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const ScubaDeepScanTab = forwardRef<ScubaDeepScanTabRef, ScubaDeepScanTabProps>(
  function ScubaDeepScanTab({ tenantId, onStatusChange }, ref) {
    const [scanJobId, setScanJobId] = useState<string | null>(null)
    const [pollInterval, setPollInterval] = useState<number | null>(null)

    const {
      data: summary,
      isLoading: summaryLoading,
      refetch: refetchSummary,
    } = useQuery({
      queryKey: ['scuba-summary', tenantId],
      queryFn: () => api.getScubaSummary(tenantId!),
      enabled: !!tenantId,
    })

    const {
      data: report,
      isLoading: reportLoading,
      refetch: refetchReport,
    } = useQuery({
      queryKey: ['scuba-report', tenantId],
      queryFn: () => api.getScubaReport(tenantId!),
      enabled: !!tenantId && summary?.hasReport === true,
    })

    const { data: jobStatus } = useQuery({
      queryKey: ['scan-job', scanJobId],
      queryFn: () => api.getScanJobStatus(scanJobId!),
      enabled: !!scanJobId,
      refetchInterval: pollInterval || false,
    })

    useEffect(() => {
      if (!jobStatus) return
      if (jobStatus.status === 'Completed') {
        setPollInterval(null)
        setScanJobId(null)
        toast.success('SCuBA scan complete.')
        onStatusChange?.('completed')
        refetchSummary()
        refetchReport()
      } else if (jobStatus.status === 'Failed') {
        setPollInterval(null)
        setScanJobId(null)
        toast.error(`SCuBA scan failed: ${jobStatus.errorMessage || 'Unknown error'}`)
        onStatusChange?.('error')
      } else if (jobStatus.status === 'Running' || jobStatus.status === 'Queued') {
        setPollInterval(3000)
        onStatusChange?.('scanning')
      }
    }, [jobStatus, refetchSummary, refetchReport, onStatusChange])

    const startScanMutation = useMutation({
      mutationFn: () => api.startScubaScan(tenantId!),
      onSuccess: (response) => {
        setScanJobId(response.jobId)
        setPollInterval(3000)
        onStatusChange?.('scanning')
        toast.success('SCuBA scan started. This takes a few minutes.')
      },
      onError: (error: Error) => {
        onStatusChange?.('error')
        toast.error(`SCuBA scan did not start: ${error.message || 'Unknown error'}`)
      },
    })

    const handleStartScan = useCallback(() => {
      if (!tenantId) {
        toast.error('Select a tenant first.')
        return
      }
      startScanMutation.mutate()
    }, [tenantId, startScanMutation])

    const handleRefresh = useCallback(() => {
      refetchSummary()
      refetchReport()
    }, [refetchSummary, refetchReport])

    useImperativeHandle(
      ref,
      () => ({ refresh: handleRefresh, startScan: handleStartScan }),
      [handleRefresh, handleStartScan],
    )

    const isScanning =
      scanJobId !== null &&
      (jobStatus?.status === 'Running' || jobStatus?.status === 'Queued')
    const scanInProgress = isScanning || startScanMutation.isPending

    /* ----------- No tenant ------------------------------------------------- */
    if (!tenantId) {
      return (
        <EmptyState
          headline="Choose a tenant to run a SCuBA deep scan."
          body="Pick one in the tenant switcher, then run the audit from here."
          headingLevel="h1"
          className="mt-8"
        />
      )
    }

    /* ----------- Loading --------------------------------------------------- */
    if (summaryLoading) {
      return (
        <div className="mt-8 max-w-[56ch]">
          <Skeleton lines={3} label="Loading SCuBA results" />
        </div>
      )
    }

    /* ----------- No report yet: offer the audit --------------------------- */
    if (!summary?.hasReport) {
      return (
        <IntroHero
          onStart={handleStartScan}
          isScanning={scanInProgress}
          currentStep={jobStatus?.currentStep}
        />
      )
    }

    /* ----------- Main report view ----------------------------------------- */
    const score = Math.round(summary?.complianceScore ?? 0)

    // SCuBA-specific brief. The parent SecurityCenter renders no Guardian
    // composite header on this tab, so this brief owns the verdict. The
    // aside replaces the score tile: SCuBA findings don't roll into Guardian
    // Score, so a 0-100 Guardian-style tile would mis-frame the page.
    const scubaBrief = composeScubaBrief(summary, report ?? null)
    const products = report?.products ?? summary?.products ?? []

    return (
      <div>
        <PillarBrief
          brief={scubaBrief}
          score={null}
          components={[]}
          tone="bpa"
          aside={<ScubaScanAside score={score} summary={summary} />}
          secondaryAction={
            <Button kind="tertiary" onClick={handleStartScan} loading={scanInProgress}>
              {scanInProgress ? jobStatus?.currentStep || 'Running the audit' : 'Run a new audit'}
            </Button>
          }
          footerNote="SCuBA findings don't roll into **Guardian Score**, and **Best Practice** tracks alignment continuously. Some CISA baselines are stricter than your tenant may need, so ask iuvo which to act on."
        />

        {products.length > 0 ? (
          <CondensedGrid
            as="ul"
            aria-label="SCuBA results by workload"
            columns={{ base: 1, 'c-md': 2, 'c-lg': 3, 'c-xlg': 5 }}
            className="mt-8"
          >
            {products.map((product: ScubaProductResult | ScubaProductSummary) => (
              <WorkloadTile key={product.productName} product={product} />
            ))}
          </CondensedGrid>
        ) : null}

        <ScubaChecksTable
          report={report ?? null}
          isLoading={reportLoading}
          summary={summary}
        />
      </div>
    )
  },
)

export default ScubaDeepScanTab

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

function IntroHero({
  onStart,
  isScanning,
  currentStep,
}: {
  onStart: () => void
  isScanning: boolean
  currentStep?: string | null
}) {
  return (
    <>
      <section aria-labelledby="scuba-intro-headline" className="mt-8">
        <BriefHero
          brief={composeScubaBrief(null)}
          size="pillar"
          headingId="scuba-intro-headline"
          primaryAction={{
            label: isScanning ? currentStep || 'Running the audit' : 'Run the CISA deep audit',
            onClick: onStart,
            loading: isScanning,
          }}
        />
        <p className="m-0 mt-5 max-w-[56ch] text-body text-text-secondary">
          SCuBA checks Entra ID, SharePoint, Exchange Online, Teams and Defender against CISA&rsquo;s Secure Cloud
          Business Applications baselines. The audit takes a few minutes and changes no tenant configuration.
        </p>
      </section>

      <section className="mt-12">
        <SectionHeadline level="page">Workloads the audit covers</SectionHeadline>
        <CondensedGrid as="ul" columns={{ base: 1, 'c-md': 3, 'c-xlg': 5 }} className="mt-4">
          {(['aad', 'sharepoint', 'exo', 'teams', 'defender'] as const).map((product) => {
            const info = getProductInfo(product)
            return (
              <Tile as="li" key={product}>
                <TileHeader
                  title={info.displayName}
                  level={3}
                  lead={<span className="text-[color:var(--icon-secondary)]">{getProductIcon(product)}</span>}
                />
              </Tile>
            )
          })}
        </CondensedGrid>
      </section>

      <p className="m-0 mt-8 max-w-[56ch] text-body text-text-secondary">
        For continuous SCuBA alignment, open{' '}
        <Link to="/security?tab=best-practices" className="text-link hover:text-link-hover hover:underline">
          Best Practice
        </Link>
        . Guardian checks it twice a day.
      </p>
    </>
  )
}

/** One workload: its pass rate, the pass count and an 8px bar (mockup 03 framework tile). */
function WorkloadTile({ product }: { product: ScubaProductResult | ScubaProductSummary }) {
  const info = getProductInfo(product.productName)
  const pct = Math.round(product.complianceScore)
  const warnings = 'warnings' in product ? product.warnings : 0
  return (
    <Tile as="li" className="flex min-w-0 flex-col pb-5">
      <TileHeader
        title={info.displayName}
        level={3}
        lead={<span className="text-[color:var(--icon-secondary)]">{getProductIcon(product.productName)}</span>}
      />
      <BigNumber value={pct} suffix="%" size="figure" className="mt-3" />
      <p className="m-0 mt-0.5 text-helper text-text-helper">
        <span className="tabular-nums">
          {n(product.passes)} of {n(product.totalControls)}
        </span>{' '}
        checks pass
        {product.failures > 0 ? ` · ${n(product.failures)} failing` : ''}
        {warnings > 0 ? ` · ${n(warnings)} ${plural(warnings, 'warning', 'warnings')}` : ''}
      </p>
      <ScoreBar value={pct} label={`${info.displayName} SCuBA pass rate`} className="mt-4" />
    </Tile>
  )
}

/**
 * The tile beside the brief. SCuBA findings don't roll into Guardian Score,
 * so it frames the audit instead: the pass rate across the CISA checks and
 * the checks by result as a proportional meter (failed in the strong neutral,
 * passed in the unearned gray, the MFA-bar pattern).
 */
function ScubaScanAside({ score, summary }: { score: number; summary: ScubaSummary }) {
  const totalControls = summary.totalControls ?? 0
  const manual = summary.totalManual ?? 0

  return (
    <Tile
      as="aside"
      padding={24}
      aria-label="SCuBA audit snapshot"
      className="flex flex-col self-start max-c-lg:max-w-[640px]"
    >
      <TileHeader title="CISA SCuBA audit" level={2} lead={<span className="text-[color:var(--icon-secondary)]">{getProductIcon('aad')}</span>} />
      <BigNumber value={score} suffix="%" size="score" className="mt-4" />
      <p className="m-0 mt-1 text-helper text-text-helper">
        compliance across <span className="tabular-nums">{n(totalControls)}</span> CISA SCuBA checks
      </p>
      <SegmentedMeter
        className="mt-6"
        caption="Checks by result"
        segments={[
          { id: 'fail', value: summary.totalFailures ?? 0, tone: 'strong', label: 'failing' },
          { id: 'warn', value: summary.totalWarnings ?? 0, tone: 'med', label: plural(summary.totalWarnings ?? 0, 'warning', 'warnings') },
          { id: 'error', value: summary.totalErrors ?? 0, tone: 'low', label: plural(summary.totalErrors ?? 0, 'error', 'errors') },
          { id: 'pass', value: summary.totalPasses ?? 0, tone: 'off', label: 'passing' },
        ]}
      />
      {manual > 0 ? (
        <p className="m-0 mt-3 text-helper text-text-helper">
          <span className="tabular-nums">{n(manual)}</span> more {plural(manual, 'needs', 'need')} a manual check.
        </p>
      ) : null}
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* The checks table                                                           */
/* -------------------------------------------------------------------------- */

interface CheckRow {
  key: string
  productName: string
  workload: string
  group: ScubaControlGroup
  control: ScubaControl
}

type ResultTab = 'fail' | 'warn' | 'error' | 'pass' | 'all'

const TAB_RESULT: Record<Exclude<ResultTab, 'all'>, ScubaControlResult> = {
  fail: 'Fail',
  warn: 'Warning',
  error: 'Error',
  pass: 'Pass',
}

const PAGE_SIZE = 25

/** SCuBA criticality ("Shall", "Should/Not-Implemented", …) to a severity level: Shall reads critical, Should high, May medium. */
const criticalityLevel = (criticality: string | undefined) => severityFromApi((criticality ?? '').split('/')[0])

function ResultTag({ result }: { result: ScubaControlResult }) {
  switch (result) {
    case 'Pass':
      return <Tag icon={<IconCheck />}>Pass</Tag>
    case 'Fail':
      return <Tag kind="high-contrast">Fail</Tag>
    case 'Warning':
      return <Tag>Warning</Tag>
    case 'Error':
      return <Tag kind="outline" title="SCuBA could not evaluate this check">Error</Tag>
    case 'NotApplicable':
    default:
      return (
        <Tag kind="outline" title="Not applicable, or needs a manual check">
          N/A
        </Tag>
      )
  }
}

function ScubaChecksTable({
  report,
  isLoading,
  summary,
}: {
  report: ScubaReport | null
  isLoading: boolean
  summary: ScubaSummary
}) {
  const rows = useMemo<CheckRow[]>(() => {
    if (!report) return []
    return report.products.flatMap(product => {
      const workload = getProductInfo(product.productName).displayName
      return (product.controlGroups ?? []).flatMap(group =>
        (group.controls ?? []).map((control, idx) => ({
          key: `${product.productName}-${group.groupNumber}-${control.controlId || idx}-${idx}`,
          productName: product.productName,
          workload,
          group,
          control,
        })),
      )
    })
  }, [report])

  const counts = useMemo(() => {
    const c: Record<ResultTab, number> = { fail: 0, warn: 0, error: 0, pass: 0, all: rows.length }
    for (const r of rows) {
      if (r.control.result === 'Fail') c.fail++
      else if (r.control.result === 'Warning') c.warn++
      else if (r.control.result === 'Error') c.error++
      else if (r.control.result === 'Pass') c.pass++
    }
    return c
  }, [rows])

  const defaultTab: ResultTab = counts.fail > 0 ? 'fail' : counts.warn > 0 ? 'warn' : 'all'
  const [tab, setTab] = useState<ResultTab | null>(null)
  const activeTab = tab ?? defaultTab
  const [search, setSearch] = useState('')
  const [workload, setWorkload] = useState('all')
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [activeTab, search, workload, report])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(r => {
      if (activeTab !== 'all' && r.control.result !== TAB_RESULT[activeTab]) return false
      if (workload !== 'all' && r.productName !== workload) return false
      if (!q) return true
      return `${r.control.requirement} ${r.control.controlId} ${r.group.groupName} ${r.workload}`.toLowerCase().includes(q)
    })
  }, [rows, activeTab, workload, search])

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const productOptions = (report?.products ?? summary.products ?? []).map(p => ({
    value: p.productName,
    label: getProductInfo(p.productName).displayName,
  }))

  const columns: DataColumn<CheckRow>[] = [
    {
      id: 'severity',
      header: 'Severity',
      nowrap: true,
      cell: r => <SeverityGlyph level={criticalityLevel(r.control.criticality)} />,
    },
    {
      id: 'check',
      header: 'Check',
      primary: true,
      cell: r => (
        <TableItem
          title={<span className="break-words">{r.control.requirement}</span>}
          sub={
            <>
              <span className="font-mono">{r.control.controlId}</span> · {r.group.groupName}
            </>
          }
        />
      ),
    },
    {
      id: 'workload',
      header: 'Workload',
      inlineOnPhone: true,
      cell: r => r.workload,
    },
    {
      id: 'result',
      header: 'Result',
      inlineOnPhone: true,
      cell: r => <ResultTag result={r.control.result} />,
    },
  ]

  const totalControls = summary.totalControls ?? rows.length
  const workloadCount = (report?.products ?? summary.products ?? []).length

  return (
    <div id="scuba-checks" className="mt-12 scroll-mt-24">
      <DataTable
        title="SCuBA checks"
        description={
          <>
            <b className="font-semibold tabular-nums text-text-primary">{n(totalControls)}</b> CISA checks across{' '}
            {workloadCount} {plural(workloadCount, 'workload', 'workloads')}. {n(summary.totalPasses ?? 0)} pass.
          </>
        }
        tabs={
          <ContainedTabs
            aria-label="Result"
            value={activeTab}
            onChange={id => setTab(id as ResultTab)}
            tabs={[
              { id: 'fail', label: 'Failing', count: isLoading ? null : n(counts.fail) },
              { id: 'warn', label: 'Warnings', count: isLoading ? null : n(counts.warn) },
              { id: 'error', label: 'Errors', count: isLoading ? null : n(counts.error) },
              { id: 'pass', label: 'Passing', count: isLoading ? null : n(counts.pass) },
              { id: 'all', label: 'All', count: isLoading ? null : n(counts.all) },
            ]}
          />
        }
        toolbar={
          <>
            <TableSearch value={search} onChange={setSearch} label="Search checks" />
            <TableSelect
              label="Workload"
              value={workload}
              onChange={setWorkload}
              options={[{ value: 'all', label: 'All' }, ...productOptions]}
            />
          </>
        }
        columns={columns}
        rows={pageRows}
        rowId={r => r.key}
        rowLabel={r => r.control.controlId || r.control.requirement}
        isLoading={isLoading}
        expandable={{
          isExpandable: r => !!r.control.details || !!r.group.groupReferenceUrl,
          render: r => <CheckDetail row={r} />,
        }}
        empty={
          !isLoading && !report
            ? 'The detailed SCuBA report did not load. Refresh the page to try again.'
            : activeTab === 'fail' && counts.fail === 0 && !search && workload === 'all'
              ? 'Nothing to review.'
              : 'Nothing to review in this view.'
        }
        pagination={
          filtered.length > PAGE_SIZE
            ? {
                page,
                pageSize: PAGE_SIZE,
                total: filtered.length,
                onPageChange: setPage,
                itemLabel: plural(filtered.length, 'check', 'checks'),
              }
            : undefined
        }
      />
    </div>
  )
}

function CheckDetail({ row }: { row: CheckRow }) {
  const { control, group } = row
  return (
    <RowDetail
      columns={[
        <RowDetailSection key="found" title="What SCuBA found">
          {control.details ? (
            <p
              className="m-0 break-words [&_a]:text-link [&_a]:underline hover:[&_a]:text-link-hover"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(control.details) }}
            />
          ) : (
            <p className="m-0">SCuBA recorded no detail for this check.</p>
          )}
        </RowDetailSection>,
        <RowDetailSection key="baseline" title="Baseline">
          <p className="m-0">
            <b>
              {group.groupNumber}. {group.groupName}
            </b>{' '}
            in the {row.workload} baseline.
          </p>
          {group.groupReferenceUrl ? (
            <ButtonLink href={group.groupReferenceUrl} kind="ghost" size="sm" icon={<IconExternal />} className="-ml-3 mt-2">
              Read the CISA baseline
            </ButtonLink>
          ) : null}
        </RowDetailSection>,
      ]}
    />
  )
}
